import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import { useQuery } from 'hooks/useCustomQuery';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
// import OCCNameField from './formFields/OCCNameField';
import OCCVisibleScopeField from './formFields/OCCVisibleScopeField';
import OCCOperatorsField from './formFields/OCCOperatorsField';
import OCCCallingProfilesField from './formFields/OCCCallingProfilesField';
import FormBase from '@/components/forms/new/FormBase';
import { multiSelectDataMapper, query, toSentenceCase } from 'utils/utils';
// Removed unused legacy imports
import createServiceProfile from 'API/ServiceProfiles/createServiceProfile';
import ServiceProfileLoader from 'pages/services/serviceprofiles/ServiceProfileLoader';

import { axios } from '@/services/axios';
import getServiceProfiles from 'API/getServiceProfiles';
import updateServiceProfiles from 'API/ServiceProfiles/updateServiceProfiles';

import walkthroughIds from '../../walkthroughIds';
import { defaultChakraSelectStyle, chakraSelectDarkMode } from '@/constants';
import BreadcrumbTrail from 'components/BreadcrumbTrail';
import useGetCurrentWholesaler from '@/queries/wholesaler/useGetCurrentWholesaler';
import { resolveWholesalerProfileName } from './serviceProfileName';
// import config from '@/config.json';

export default function OCC({
    mode,
    data,
    handleBackButton,
    level,
    visibleScopeOptions,
}) {
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const { darkMode } = useSelector((state) => state.settings);
    const { requiredScope } = useSelector((state) => state.login);
    const {
        currentWholesaler: wholesalerID,
        currentPartner: partnerID,
        companiesAndPartners,
    } = useSelector((state) => state.navigationLists);
    const { data: currentWholesalerData } = useGetCurrentWholesaler();

    const DEFAULT_OCC_PAYLOAD = {
        name: resolveWholesalerProfileName(
            null,
            'Operator Connect Conferencing',
        ),
        serviceType: 'OperatorConnectConferencing',
        callingProfiles: [],
        operatorProfiles: [],
        partnerID: level === 'partner' ? partnerID : undefined,
        wholesalerID: level === 'partner' ? undefined : wholesalerID,
        visibleScope: level === 'partner' ? 20 : 60,
    };

    const { ...ufElements } = walkthroughIds.serviceProfiles;
    const chakraSelectDarkStyle = chakraSelectDarkMode(darkMode);
    const chakraStyles = {
        ...defaultChakraSelectStyle,
        ...chakraSelectDarkStyle,
    };

    const [modalState, setModalState] = useState(data || DEFAULT_OCC_PAYLOAD);

    // Helper to map value(s) to option(s) for select fields
    const mapToOptions = (values, options) => {
        if (!values || !options) return [];
        if (Array.isArray(values)) {
            return (
                values
                    .map((v) => options.find((o) => o.value === (v.value ?? v)))
                    ?.filter(Boolean) || []
            );
        }
        return (
            options.find((o) => o.value === (values.value ?? values)) || null
        );
    };

    useEffect(() => {
        if (
            mode !== 'EDIT' &&
            level !== 'partner' &&
            currentWholesalerData?.name
        ) {
            setModalState((prev) => ({
                ...prev,
                name: resolveWholesalerProfileName(
                    currentWholesalerData?.name,
                    'Operator Connect Conferencing',
                ),
            }));
        } else if (
            mode !== 'EDIT' &&
            level === 'partner' &&
            companiesAndPartners?.length > 0
        ) {
            const partnerName = companiesAndPartners.find(
                ({ id }) => id === partnerID,
            )?.name;
            setModalState((prev) => ({
                ...prev,
                name: `${partnerName} - Operator Connect Conferencing Partner Profile`,
            }));
        }
    }, [
        mode,
        companiesAndPartners,
        currentWholesalerData?.name,
        level,
        partnerID,
        wholesalerID,
    ]);

    // Helper to get scope string
    const scope =
        level === 'partner'
            ? `Partner/${data?.partnerID || partnerID}`
            : `Wholesaler/${data?.wholesalerID || wholesalerID}`;

    const { data: operatorsEnum = [], isLoading: operatorsLoading } = useQuery({
        queryKey: ['occOperatorProfiles', scope],
        queryFn: async () => {
            const res = await axios.get(
                `/ServiceProfiles/OperatorConnectConferencing/${scope}/OperatorProfiles/Available`,
            );
            return res.data.map(multiSelectDataMapper);
        },
    });

    const { isInitialLoading: profileLoading } = useQuery({
        queryKey: ['occSPData', data?.id, mode],
        queryFn: () => getServiceProfiles({ level, id: data.id }),
        enabled: mode === 'EDIT' && Boolean(data),
        // FIX:(3212): drop the cache on unmount so every open refetches the
        // saved profile instead of reusing data from a previous open.
        cacheTime: 0,
        onSuccess: (spResult) => {
            setModalState({
                ...spResult.data,
                callingProfiles:
                    spResult.data.callingProfiles?.map(multiSelectDataMapper) ||
                    [],
                operatorProfiles:
                    spResult.data.operatorProfiles?.map(
                        multiSelectDataMapper,
                    ) || [],
            });
        },
    });

    const operatorProfileIds = (modalState.operatorProfiles || [])
        .map((op) => op?.value ?? op)
        .filter(Boolean);

    const { data: callingProfiles = [] } = useQuery({
        queryKey: ['occCallingProfiles', scope, operatorProfileIds],
        queryFn: async () => {
            const res = await axios.get(
                `/ServiceProfiles/OperatorConnectConferencing/${scope}/CallingProfiles/Available?OperatorProfileIDs=${query(modalState.operatorProfiles)}`,
            );
            return res.data.map(multiSelectDataMapper);
        },
        enabled: operatorProfileIds.length > 0,
    });

    // Formik initial values (match ACS pattern)
    const initialValues = {
        name: modalState?.name || '',
        operatorProfiles: mapToOptions(
            modalState?.operatorProfiles,
            operatorsEnum,
        ),
        callingProfiles: mapToOptions(
            modalState?.callingProfiles,
            callingProfiles,
        ),
        partnerID:
            level === 'partner'
                ? (modalState?.partnerID ?? partnerID)
                : undefined,
        wholesalerID:
            level === 'partner'
                ? undefined
                : (modalState?.wholesalerID ?? wholesalerID),
        visibleScope:
            modalState?.visibleScope ?? (level === 'partner' ? 20 : 60),
    };

    if (operatorsLoading) return <ServiceProfileLoader />;
    const validate = () => {
        const state = modalState || {};
        const errors = {};
        if (!state.name) errors.name = 'Name is required';
        if (!state.visibleScope && state.visibleScope !== 0)
            errors.visibleScope = 'Visible Scope is required';
        if (!state.operatorProfiles || state.operatorProfiles.length === 0)
            errors.operatorProfiles = 'At least one operator is required';
        if (!state.callingProfiles || state.callingProfiles.length === 0)
            errors.callingProfiles = 'At least one calling profile is required';
        // setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = (_values, actions) => {
        setError(null);
        setSubmitted(true);
        if (!validate()) {
            setError(
                'There are some validation errors in the form. Please check and try again.',
            );
            actions?.setSubmitting(false);
            return;
        }
        setIsLoading(true);
        const params = {
            type: 'OperatorConnectConferencing',
            payload: modalState,
            level,
            scope: requiredScope,
        };
        let result;
        if (mode === 'EDIT') {
            result = updateServiceProfiles(params);
        } else {
            result = createServiceProfile(params);
        }
        result
            .then(() => handleBackButton())
            .catch((e) =>
                setError(
                    e?.response?.data?.message ||
                        e?.message ||
                        'An error occurred. Please try again later.',
                ),
            )
            .finally(() => {
                setIsLoading(false);
                actions?.setSubmitting(false);
            });
    };

    if (isLoading || profileLoading) return <ServiceProfileLoader />;
    const breadCrumbTrail = [
        { label: 'Services', path: ['', { index: 0 }] },
        { label: 'Configurations', path: '' },
    ];
    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`${toSentenceCase(mode)} ${modalState.name || data?.name || ''}`}
                subTitleHeader={true}
                backLink={['/service-profiles', { state: { index: 0 } }]}
                ufBackBtn="builderBackBtn"
                ufSaveBtn="builderSaveBtn"
                customClick={true}
                initialValues={initialValues}
                onBack={handleBackButton}
                isLoading={isLoading}
                finalFormSubmit={handleSubmit}>
                {({ errors = {}, touched = {} }) =>
                    [
                        <OCCVisibleScopeField
                            key="visibleScope"
                            value={modalState.visibleScope}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    visibleScope: val,
                                }))
                            }
                            options={visibleScopeOptions}
                            darkMode={darkMode}
                            minWidth="420px"
                            maxWidth="520px"
                            width="100%"
                            errors={errors}
                            touched={touched}
                        />,
                        <OCCOperatorsField
                            key="operators"
                            value={modalState.operatorProfiles}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    operatorProfiles: val,
                                }))
                            }
                            options={operatorsEnum}
                            chakraStyles={chakraStyles}
                            walkthroughId={ufElements.ufOperatorsSelect}
                            disableStackHeight={true}
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                            error={
                                !modalState.operatorProfiles ||
                                modalState.operatorProfiles.length === 0
                                    ? 'At least one operator is required'
                                    : ''
                            }
                            showError={submitted}
                        />,
                        <OCCCallingProfilesField
                            key="callingProfiles"
                            value={modalState.callingProfiles}
                            disableStackHeight={true}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    callingProfiles: val,
                                }))
                            }
                            options={callingProfiles}
                            chakraStyles={chakraStyles}
                            walkthroughId={ufElements.ufCallingProfilesSelect}
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                            error={
                                !modalState.callingProfiles ||
                                modalState.callingProfiles.length === 0
                                    ? 'At least one calling profile is required'
                                    : ''
                            }
                            showError={submitted}
                        />,
                        error && (
                            <Box paddingBottom="0.5rem" key="error-box-occ">
                                <Alert
                                    key="error"
                                    status="error"
                                    mt={4}
                                    marginBottom="1rem">
                                    <AlertIcon />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            </Box>
                        ),
                    ].filter(Boolean)
                }
            </FormBase>
        </>
    );
}
